import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function RealisationsPromptCard({ prompt, onDone }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const options = useMemo(() => prompt?.options || [], [prompt]);

  if (!prompt) return null;

  const toggle = (code) => {
    setSelected((current) => {
      if (current.includes(code)) {
        return current.filter((x) => x !== code);
      }
      if (code === "just_exploring") return ["just_exploring"];
      const withoutExploring = current.filter((x) => x !== "just_exploring");
      if (withoutExploring.length >= 3) return withoutExploring;
      return [...withoutExploring, code];
    });
  };

  const answer = async () => {
    if (!selected.length || saving) return;
    setSaving(true);
    try {
      await api.post(
        "/realisations/prompts/" + prompt.prompt_id + "/answer",
        { reason_codes: selected }
      );
      toast.success("Thanks — that helps us understand your choices.");
      onDone?.();
    } catch {
      toast.error("We couldn't save that just now.");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(
        "/realisations/prompts/" + prompt.prompt_id + "/dismiss"
      );
      onDone?.();
    } catch {
      toast.error("We couldn't dismiss that just now.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="mt-6 border-stone-200 bg-[#FDFBF7] p-5 sm:p-6"
      data-testid="realisations-prompt-card"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#4C1D95]/10 p-2 text-[#4C1D95]">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-uppercase text-stone-500">A quick thought</div>
          <h2 className="font-editorial text-xl sm:text-2xl mt-1 text-stone-900">
            {prompt.question}
          </h2>
          {prompt.target?.subtitle && (
            <p className="text-xs text-stone-500 mt-1">
              {prompt.target.subtitle}
            </p>
          )}
          <p className="text-sm text-stone-600 mt-2">{prompt.helper_text}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {options.map((option) => {
              const active = selected.includes(option.code);
              return (
                <button
                  key={option.code}
                  type="button"
                  aria-pressed={active}
                  data-testid={"realisations-reason-" + option.code}
                  onClick={() => toggle(option.code)}
                  className={
                    "rounded-full border px-3 py-1.5 text-sm transition-colors " +
                    (active
                      ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                      : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
                  }
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Button
              type="button"
              onClick={answer}
              disabled={!selected.length || saving}
              data-testid="realisations-prompt-save"
              className="bg-[#002147] hover:bg-[#001A38] text-white"
            >
              Save
            </Button>
            <button
              type="button"
              onClick={dismiss}
              disabled={saving}
              data-testid="realisations-prompt-skip"
              className="text-sm text-stone-500 hover:text-stone-800 disabled:opacity-50"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
